import type { Ref } from 'vue';
import type { BlockSerialized, BlockSerializedChild } from '#layers/block-engine/types/block';
import type { BlockProgrammaticChildRegistration } from '#layers/block-engine/utils/block/api/programmatic-child';
import { computed, ref, watch } from 'vue';
import { isBlockProgrammaticChildInspectable, isBlockProgrammaticChildSelectable } from '#layers/block-engine/utils/block/api/programmatic-child';

export function useBlockEditorSelection({
	blockData,
	programmaticChildren = ref(new Map<string, BlockProgrammaticChildRegistration>()),
}: {
	blockData: Ref<BlockSerialized | null>;
	/**
	 * Programmatic child registry. Plain editor usages default to an empty registry.
	 */
	programmaticChildren?: Ref<Map<string, BlockProgrammaticChildRegistration>>;
}) {
	const selectedChildIds = ref<Array<string>>([]);

	function isSelectableChild(childId: string | null | undefined) {
		return isBlockProgrammaticChildSelectable(programmaticChildren.value, childId ?? undefined);
	}

	function isInspectableChild(childId: string | null | undefined) {
		return isBlockProgrammaticChildInspectable(programmaticChildren.value, childId ?? undefined);
	}

	const selectedChildren = computed(() => selectedChildIds.value.map((childId) => {
		return blockData.value?.children?.[childId] ?? null;
	}).filter(Boolean) as Array<BlockSerializedChild>);
	const firstSelectedChild = computed(() => {
		const selectedChildId = selectedChildIds.value.find((childId) => isInspectableChild(childId));

		if (!selectedChildId) {
			return;
		}

		return selectedChildren.value.find((child) => child.id === selectedChildId);
	});

	function add(childId: string) {
		if (!selectedChildIds.value.includes(childId)) {
			selectedChildIds.value.push(childId);
		}
	}

	function remove(childId: string) {
		const index = selectedChildIds.value.indexOf(childId);

		if (index !== -1) {
			selectedChildIds.value.splice(index, 1);
		}
	}

	function toggle(childId: string) {
		if (selectedChildIds.value.includes(childId)) {
			remove(childId);
		}
		else {
			add(childId);
		}
	}

	function clear() {
		selectedChildIds.value = [];
	}

	// Hover

	const hoveredChildId = ref<string | null>(null);
	const hoveredChild = computed(() => {
		if (hoveredChildId.value) {
			return blockData.value?.children?.[hoveredChildId.value] ?? null;
		}

		return null;
	});

	/**
	 * Hovered when the context menu was opened
	 */
	const contextMenuChildId = ref<string | null>(null);

	watch(selectedChildIds, (value) => {
		const filtered = value.filter((childId) => isSelectableChild(childId));

		if (filtered.length !== value.length) {
			selectedChildIds.value = filtered;
		}
	}, { deep: true });

	watch(hoveredChildId, (value) => {
		if (value && !isSelectableChild(value)) {
			hoveredChildId.value = null;
		}
	});

	watch(contextMenuChildId, (value) => {
		if (value && !isSelectableChild(value)) {
			contextMenuChildId.value = null;
		}
	});

	return {
		selectedChildIds,
		selectedChildren,
		firstSelectedChild,
		add,
		remove,
		toggle,
		clear,
		hoveredChildId,
		hoveredChild,
		contextMenuChildId,
	};
}

export type BlockEditorSelection = ReturnType<typeof useBlockEditorSelection>;
